/**
 * Server-owned state for the workspace, read once and shared.
 *
 * Two hooks: `useWorkspaceSnapshot` reads the three collections every page
 * draws from (the Business Profile, the Production Lines, the video
 * library) and derives the wireframe's line and video models; the intake
 * journey hook follows one Production Line's analysis exactly as the server
 * reports it — claim, confirm, poll, retry — without ever inferring a
 * stage. Nothing here simulates progress; timers only ask the server again.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { newIdempotencyKey } from '../workspace/api.js';
import {
  approveMerchantProfile,
  claimMerchantIntake,
  continueMerchantAnalysis,
  correctMerchantProfile,
  fetchCurrentMerchantProfile,
  fetchMerchantIntake,
  fetchMerchantVideos,
  fetchProductionLines,
  MERCHANT_INTAKE_STORAGE_KEY,
  MERCHANT_PROFILE_INTENT_STORAGE_KEY,
  processMerchantIntake,
  type BusinessProfileDraftLike,
  type MerchantAnalysisStepKey,
  type MerchantProfileView,
  type MerchantVideoView,
  type PendingProductionLineView,
  type ProductionLineView,
} from './api.js';
import {
  lineSummary,
  pendingLineSummary,
  videoItemOf,
  type LineSummary,
  type VideoItem,
} from './model.js';
import { missingProfileConfirmationFields } from './profileCompleteness.js';

export interface WorkspaceLists {
  readonly pending: readonly PendingProductionLineView[];
  readonly lines: readonly ProductionLineView[];
}

export interface WorkspaceSnapshot {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly profile: MerchantProfileView | null;
  readonly rawLines: readonly ProductionLineView[];
  readonly pendingLines: readonly PendingProductionLineView[];
  readonly lines: readonly LineSummary[];
  readonly videos: readonly VideoItem[];
  readonly rawVideos: readonly MerchantVideoView[];
  readonly creationDestination: string | null;
  /** Re-read everything; resolves with the fresh lists (null when disabled or unavailable). */
  readonly refresh: () => Promise<WorkspaceLists | null>;
}

export function rememberedIntakeId(): string | null {
  try {
    return localStorage.getItem(MERCHANT_INTAKE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberIntakeId(id: string): void {
  try {
    localStorage.setItem(MERCHANT_INTAKE_STORAGE_KEY, id);
  } catch {
    // Without storage the workspace still works from the server's own lists.
  }
}

export function rememberedProfileIntent(): 'confirm' | 'correct' | null {
  try {
    const value = sessionStorage.getItem(MERCHANT_PROFILE_INTENT_STORAGE_KEY);
    return value === 'confirm' || value === 'correct' ? value : null;
  } catch {
    return null;
  }
}

export function clearProfileIntent(): void {
  try {
    sessionStorage.removeItem(MERCHANT_PROFILE_INTENT_STORAGE_KEY);
  } catch {
    // The profile remains claimable; only the convenience continuation is lost.
  }
}

/** One read of everything the workspace shows, derived into the wireframe's models. */
export function useWorkspaceSnapshot(enabled: boolean): WorkspaceSnapshot {
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [profile, setProfile] = useState<MerchantProfileView | null>(null);
  const [rawLines, setRawLines] = useState<readonly ProductionLineView[]>([]);
  const [pendingLines, setPendingLines] = useState<
    readonly PendingProductionLineView[]
  >([]);
  const [rawVideos, setRawVideos] = useState<readonly MerchantVideoView[]>([]);
  const [creationDestination, setCreationDestination] = useState<string | null>(null);
  const mounted = useRef(true);

  const refresh = useCallback(async (): Promise<WorkspaceLists | null> => {
    if (!enabled) return null;
    const [profileResult, linesResult, videosResult] = await Promise.allSettled([
      fetchCurrentMerchantProfile(),
      fetchProductionLines(),
      fetchMerchantVideos(),
    ]);
    if (!mounted.current) return null;
    // A merchant with no profile yet is a valid, empty workspace — not a failure.
    if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
    if (linesResult.status === 'fulfilled') {
      setRawLines(linesResult.value.productionLines);
      setPendingLines(linesResult.value.pendingProductionLines ?? []);
      setCreationDestination(
        linesResult.value.creation.available
          ? linesResult.value.creation.destination
          : null,
      );
    }
    if (videosResult.status === 'fulfilled') setRawVideos(videosResult.value.videos);
    setFailed(linesResult.status === 'rejected');
    setLoading(false);
    return linesResult.status === 'fulfilled'
      ? {
          pending: linesResult.value.pendingProductionLines ?? [],
          lines: linesResult.value.productionLines,
        }
      : null;
  }, [enabled]);

  useEffect(() => {
    mounted.current = true;
    void refresh();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  const videos = useMemo(
    () => rawVideos.map((video) => videoItemOf(video)),
    [rawVideos],
  );

  const lines = useMemo(() => {
    const byLine = new Map<string, VideoItem[]>();
    for (const video of videos) {
      if (video.lineId === null) continue;
      const bucket = byLine.get(video.lineId) ?? [];
      bucket.push(video);
      byLine.set(video.lineId, bucket);
    }
    const intakeByLine = new Map<string, string>();
    for (const video of rawVideos) {
      if (video.productionLineId !== null)
        intakeByLine.set(video.productionLineId, video.intakeId);
    }
    const pending = pendingLines.map((line) =>
      pendingLineSummary(
        line,
        byLine.get(line.productionLineId) ?? [],
        (line as { intakeId?: string }).intakeId ??
          intakeByLine.get(line.productionLineId) ??
          null,
      ),
    );
    const configured = rawLines.map((line) =>
      lineSummary(line, byLine.get(line.productionLine.productionLineId) ?? []),
    );
    return [...pending, ...configured].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [pendingLines, rawLines, rawVideos, videos]);

  return {
    loading,
    failed,
    profile,
    rawLines,
    pendingLines,
    lines,
    videos,
    rawVideos,
    creationDestination,
    refresh,
  };
}

const MAX_ANALYSIS_POLL_MS = 30_000;

export interface IntakeJourney {
  readonly view: MerchantProfileView | null;
  readonly error: string | null;
  readonly working: boolean;
  readonly refreshing: boolean;
  readonly load: () => Promise<boolean>;
  readonly refresh: () => Promise<boolean>;
  readonly approve: () => Promise<boolean>;
  readonly correct: (
    profile: BusinessProfileDraftLike,
    reason: string,
  ) => Promise<boolean>;
  readonly retryStep: (key: MerchantAnalysisStepKey) => Promise<boolean>;
  readonly clearError: () => void;
}

/**
 * Follow one intake: read it, claim it for the signed-in merchant, keep it
 * current while the server says polling is worthwhile, and expose the two
 * merchant decisions (confirm, correct) plus the server-authorised retry.
 */
export function useIntakeJourney(
  intakeId: string | null,
  options: {
    readonly merchant: boolean;
    readonly onChange?: (view: MerchantProfileView) => void;
  },
): IntakeJourney {
  const { merchant, onChange } = options;
  const [view, setView] = useState<MerchantProfileView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const failedPolls = useRef(0);
  const changeListener = useRef(onChange);
  changeListener.current = onChange;

  const load = useCallback(async (): Promise<boolean> => {
    if (intakeId === null) return false;
    if (inFlight.current !== null) return inFlight.current;
    const abort = new AbortController();
    controller.current = abort;
    const request = (async () => {
      try {
        let next = await fetchMerchantIntake(intakeId, abort.signal);
        if (
          next.profile?.reviewState !== 'APPROVED' &&
          (next.status === 'COLLECTING' || next.status === 'FAILED')
        ) {
          next = await processMerchantIntake(next.id, abort.signal);
        }
        if (!abort.signal.aborted && mounted.current) {
          setView(next);
          setError(null);
          changeListener.current?.(next);
        }
        return !abort.signal.aborted;
      } catch (reason) {
        if (!abort.signal.aborted && mounted.current) {
          setError(reason instanceof Error ? reason.message : 'Profile unavailable.');
        }
        return false;
      }
    })();
    inFlight.current = request;
    void request.finally(() => {
      if (inFlight.current === request) inFlight.current = null;
      if (controller.current === abort) controller.current = null;
    });
    return request;
  }, [intakeId]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      return await load();
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, [load]);

  // Processing can outlive the request that started it, so keep reading.
  useEffect(() => {
    if (view?.status !== 'PROCESSING') return undefined;
    const timer = window.setTimeout(() => void load(), 1_500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [load, view?.status]);

  // Claim the intake for the signed-in merchant, once.
  useEffect(() => {
    if (intakeId === null || view === null || view.claimed || !merchant) return;
    setWorking(true);
    void claimMerchantIntake(intakeId)
      .then((claimed) => {
        if (!mounted.current) return;
        setView(claimed);
        changeListener.current?.(claimed);
      })
      .catch((reason: unknown) => {
        if (mounted.current)
          setError(reason instanceof Error ? reason.message : 'Claim failed.');
      })
      .finally(() => {
        if (mounted.current) setWorking(false);
      });
  }, [intakeId, merchant, view]);

  // The durable server runner owns continuation; this only follows it.
  useEffect(() => {
    if (
      view?.profile?.reviewState !== 'APPROVED' ||
      !view.analysis.progress.polling.recommended ||
      view.analysis.progress.polling.afterMs === null
    ) {
      return undefined;
    }
    const after = view.analysis.progress.polling.afterMs;
    let cancelled = false;
    let timer: number | undefined;
    const poll = async () => {
      if (!navigator.onLine) {
        failedPolls.current = Math.min(failedPolls.current + 1, 4);
      } else {
        const ok = await load();
        failedPolls.current = ok ? 0 : Math.min(failedPolls.current + 1, 4);
      }
      if (!cancelled) {
        timer = window.setTimeout(
          () => void poll(),
          Math.min(after * 2 ** failedPolls.current, MAX_ANALYSIS_POLL_MS),
        );
      }
    };
    timer = window.setTimeout(() => void poll(), after);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [
    load,
    view?.profile?.reviewState,
    view?.analysis.progress.polling.afterMs,
    view?.analysis.progress.polling.recommended,
  ]);

  const approve = useCallback(async () => {
    if (view?.profile === null || view === null) return false;
    const missing = missingProfileConfirmationFields(view.profile.payload);
    if (missing.length > 0) {
      setError(
        `Correct the profile before confirming it. Missing: ${missing.join(', ')}.`,
      );
      return false;
    }
    setWorking(true);
    try {
      await approveMerchantProfile(
        view.id,
        view.profile.artifactId,
        newIdempotencyKey(),
      );
      await load();
      setError(null);
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Approval failed.');
      return false;
    } finally {
      if (mounted.current) setWorking(false);
    }
  }, [load, view]);

  const correct = useCallback(
    async (profile: BusinessProfileDraftLike, reason: string) => {
      if (view?.profile === null || view === null) return false;
      setWorking(true);
      try {
        await correctMerchantProfile(
          view.id,
          { expectedArtifactId: view.profile.artifactId, profile, reason },
          newIdempotencyKey(),
        );
        await load();
        setError(null);
        return true;
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Correction failed.');
        return false;
      } finally {
        if (mounted.current) setWorking(false);
      }
    },
    [load, view],
  );

  const retryStep = useCallback(
    async (_key: MerchantAnalysisStepKey) => {
      if (intakeId === null) return false;
      setWorking(true);
      try {
        await continueMerchantAnalysis(intakeId);
        await load();
        setError(null);
        return true;
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : 'Retry failed.');
        return false;
      } finally {
        if (mounted.current) setWorking(false);
      }
    },
    [intakeId, load],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    view,
    error,
    working,
    refreshing,
    load,
    refresh,
    approve,
    correct,
    retryStep,
    clearError,
  };
}

/** Whether the browser believes it is online; the server keeps working either way. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => {
      setOnline(true);
    };
    const off = () => {
      setOnline(false);
    };
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
